// /diaries — feed of diaries (with cursor pagination + privacy filter).
// /diaries/trash — soft-deleted (author-only) listing.
//
// Renders the active list (next-page via cursor).  Click a row to open
// the detail page (DiaryDetail).
//
// W32 polish: aligned with /anniversaries polish (W31).
//   - `pb-32` on the page root so the PillNav never covers content
//   - PageHero `eyebrow="共 N 篇"` shows total count (mirrors HeroSection
//     segment 3 「共 N 個」)
//   - PageHero `trailing` slot hosts the `+` button (matches AnniversaryHero
//     `trailing={<Link to="/anniversaries/new"><Plus/></Link>}`)
//   - DiaryFilter (SwipeableChipGroup, accent="ink-1") replaces the legacy
//     "含私人日記" checkbox.  Scope axis: 全部 / 含私人 / 我的 / 對方的.
//   - Author chip on each card uses `bg-you-tint text-you-strong` /
//     `bg-partner-tint text-partner-strong` (matches anniversary category tint
//     taxonomy; previously `bg-you/10` which read as a washed-out grey).
//   - PullToRefreshBar wrapper.
//   - 「垃圾桶」 moves from inline ghost-button (next to 「新增」) to a
//     small inline action on the filter row — same place the
//     AnniversaryTimeline surfaces secondary actions.

import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Plus, Trash2 } from "lucide-react";

import { PageHero } from "@/components/primitives/PageHero";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/primitives/EmptyState";
import { LoadingState } from "@/components/primitives/LoadingState";
import { ErrorState } from "@/components/primitives/ErrorState";
import { PullToRefreshBar } from "@/components/patterns/PullToRefreshBar";
import { PageContainer, SectionStack, RowGap } from "@/components/layout";
import { useToast } from "@/components/ui/toast";
import { ApiError } from "@/lib/api/pair";
import {
  listDiaries,
  listTrash,
  purgeDiary,
  restoreDiary,
  type DiaryOut,
  type DiaryListResponse,
} from "@/lib/api/diary";
import { diaryErrorMessage } from "@/lib/i18n/diary";
import { queryKeys } from "@/lib/queries/keys";
import { useAuth } from "@/lib/auth/AuthProvider";
import { DiaryFilter, type DiaryScopeFilter } from "./diaries/components/DiaryFilter";
import { DiaryCard } from "./diaries/components/DiaryCard";
import { TrashList } from "@/components/system/TrashList";
import { DIARY_RESTORE_DAYS } from "@/lib/retentionWindows";

interface DiariesPageProps {
  trash?: boolean;
}

export function DiariesPage({ trash = false }: DiariesPageProps = {}) {
  const qc = useQueryClient();
  const { push: pushToast } = useToast();
  const { user, pairId } = useAuth();
  const [scope, setScope] = useState<DiaryScopeFilter>("all");

  // Derive the API's `include_private` flag from the scope chip.
  // Backend supports only the boolean (per `listDiaries` signature); client
  // applies the author filter for "我的" / "對方的" since the backend doesn't
  // expose an author_id query param.
  const includePrivate = scope === "with-private" || scope === "mine";

  const liveQuery = useInfiniteQuery<DiaryListResponse>({
    queryKey: queryKeys.diary.list(null, includePrivate),
    queryFn: ({ pageParam }) => listDiaries(pageParam as string | null, 20, includePrivate),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.next_cursor,
    enabled: !trash,
  });

  const trashQuery = useQuery({
    queryKey: queryKeys.diary.trash(),
    queryFn: listTrash,
    enabled: trash,
  });

  const restoreMut = useMutation({
    mutationFn: (diaryId: string) => restoreDiary(diaryId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.diary.trash() });
      void qc.invalidateQueries({ queryKey: queryKeys.diary.list(null, false) });
      pushToast({ variant: "success", message: "已還原" });
    },
  });

  // W39.78. Irreversible, and the only one of the two that must also drop the
  // live list and the detail: a purged diary is gone from every surface at once,
  // and a stale feed row that 404s on tap is the exact shape of bug this round
  // started with.
  const purgeMut = useMutation({
    mutationFn: (diaryId: string) => purgeDiary(diaryId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.diary.trash() });
      void qc.invalidateQueries({ queryKey: queryKeys.diary.list(null, false) });
      void qc.invalidateQueries({ queryKey: queryKeys.diary.list(null, true) });
      pushToast({ variant: "success", message: "已永久刪除" });
    },
  });

  // W40.2 — these two used to **swallow** the failure, so `TrashList.run`'s
  // `await action()` resolved and it pushed 「已還原」: a success confirmation for
  // a restore that failed, on a row still in the bin. A *new* error keeps the
  // wording — `diaryErrorMessage` returns `err.message` when the server sent one.
  // The other three trash routes have no catch here at all; that was the bug.
  const restoreDiaryRow = async (diaryId: string) => {
    try {
      await restoreMut.mutateAsync(diaryId);
    } catch (err) {
      throw err instanceof ApiError ? new Error(diaryErrorMessage(err.code, err.message)) : err;
    }
  };

  const purgeDiaryRow = async (diaryId: string) => {
    try {
      await purgeMut.mutateAsync(diaryId);
    } catch (err) {
      throw err instanceof ApiError ? new Error(diaryErrorMessage(err.code, err.message)) : err;
    }
  };

  const refresh = async () => {
    if (trash) {
      await trashQuery.refetch();
    } else {
      await liveQuery.refetch();
    }
  };

  // Apply client-side author filter for 「我的」 / 「對方的」.  Defense in
  // depth: the backend withholds the partner's private rows on its own
  // (`visibility_clause` in the diary service, pinned by
  // `tests/test_diary_list_privacy.py`).
  //
  // W40.4: this comment used to cite
  // `test_diary.py::test_list_diaries_filters_private_from_partner`. There is no
  // `test_diary.py` in this suite — the reference had been wrong long enough
  // that it read as coverage for the one flag nobody tested, which is how
  // `include_private` stayed inverted.
  //
  // W35.5 finding D-4 (critical): these two memos MUST stay above the
  // `if (trash)` early return below.  `/diaries` and `/diaries/trash` are
  // two <Route> entries that both render `<PageErrorBoundary><DiariesPage
  // trash?/></PageErrorBoundary>` at the SAME tree position, so React
  // reconciles them as one instance instead of remounting.  When these
  // memos sat after the early return, tapping 「垃圾桶」 (line ~227)
  // re-rendered with 2 fewer hooks and React threw
  // "Rendered fewer hooks than expected" — a crash on a core flow.
  // Both memos are already trash-safe (`trash ? [] : ...`).
  const allItems: DiaryOut[] = useMemo(
    () =>
      trash
        ? []
        : // A response that is not the contracted shape reaches here unchanged —
          // the API layer casts with `data as T` and validates nothing — and a
          // bare array body makes `p.items` undefined, so `flatMap` produced
          // `[undefined, …]`. Every reader below then dereferenced it: `d.author_id`
          // threw `Cannot read properties of undefined`, which took the whole list
          // down behind the error boundary.
          //
          // Filtering here is the single place that has to know: `items` below
          // reads `author_id`, and every `DiaryCard` reads four more fields off
          // the same object.
          (liveQuery.data?.pages ?? [])
            .flatMap((p) => (Array.isArray(p?.items) ? p.items : []))
            .filter((d): d is DiaryOut => Boolean(d) && typeof d === "object"),
    [trash, liveQuery.data],
  );
  const items = useMemo(() => {
    if (trash || !user) return allItems;
    if (scope === "mine") return allItems.filter((d) => d.author_id === user.id);
    if (scope === "partner") return allItems.filter((d) => d.author_id !== user.id);
    return allItems;
  }, [allItems, scope, user, trash]);

  if (trash) {
    return (
      // W39.78. This branch used to hand-roll its own page: a `Card` per row,
      // a 還原 button, no 永久刪除, and — the defect the mobile UX sweep found
      // as an unnamed 404 — a `<Link to={`/diaries/${d.id}`}>` on the title. A
      // soft-deleted diary is 404 by definition (`read_diary` refuses any row
      // with `deleted_at` set), so **every row in this bin was a dead link**:
      // measured at runtime, one seeded row produced one 404 on the click that
      // named it. The shared `TrashList` has no link in it at all, which is why
      // the three bins that already used it never had this bug.
      <TrashList
        noun="日記垃圾桶"
        itemNoun="日記"
        parentTo="/diaries"
        parentLabel="日記"
        retentionDays={DIARY_RESTORE_DAYS}
        rows={(trashQuery.data?.items ?? []).map((row) => ({
          id: row.id,
          label: row.label,
          daysLeft: row.days_left,
          canRestore: row.can_restore,
        }))}
        isLoading={trashQuery.isLoading}
        isError={trashQuery.isError}
        errorMessage={trashQuery.isError ? "載入垃圾桶失敗" : undefined}
        onRetry={() => void trashQuery.refetch()}
        onRestore={restoreDiaryRow}
        onPurge={purgeDiaryRow}
      />
    );
  }

  const totalLoaded = allItems.length;
  const totalShown = items.length;
  // D-1's second number. Counted from `allItems` (the loaded feed), not from
  // `items` (the filtered view) — the subtitle describes the screen's content,
  // and it must not change when someone taps 「對方的」.
  const mineCount = user ? allItems.filter((d) => d.author_id === user.id).length : 0;

  return (
    // `pb-32` clears PillNav (76 pt pill + 16 pt bottom-4 + 36 pt safe headroom).
    // Per MOBILE-POLISH-W31-goal-prompt.md acceptance #2.
    <div data-testid="diaries-page" className="flex flex-col pb-32">
      <PageHero
        // W52.0 D-1 — the eyebrow is a **namespace**, not a number.
        //
        // This read `共 ${totalLoaded} 篇`, which made it the only page in the
        // app whose eyebrow answered 「有多少」 while the other five answered
        // 「這是哪裡」 — the split D-1 closed. W35.5 finding D-5 (the reason the
        // count was there at all) was that the eyebrow collapsed to
        // `undefined` on an empty feed; a constant label cannot collapse, so
        // the finding still holds.
        eyebrow="一起的文字"
        title="日記"
        // The count moves down a slot, where it is state rather than identity,
        // and gains the second number: of the diaries on screen, how many are
        // yours. That is the question the 全部 / 我的 / 對方的 filter exists to
        // answer, and the page never said the answer.
        subtitle={
          totalLoaded === 0
            ? "我們之間的文字紀錄"
            : `共 ${totalLoaded} 篇 · 我寫了 ${mineCount} 篇`
        }
        trailing={
          // W52.0 D-2 — the trash link joins the `+` in the hero's trailing
          // slot. It used to sit on the filter row's right edge, which made the
          // one control a person needs after deleting something the only
          // button on a row that has nothing to do with the filter. All four
          // module bins now converge here (wishes is the baseline).
          <div className="flex items-center gap-s-2">
            <Link
              to="/diaries/trash"
              data-testid="goto-trash"
              aria-label="日記垃圾桶"
              className="press shell-btn text-ink-2 hover:bg-black/5 transition-colors duration-quick"
            >
              <Trash2 className="w-4 h-4" aria-hidden />
            </Link>
            <Link
              to="/diaries/new"
              aria-label="新增日記"
              data-testid="diaries-add"
              className="press cta-pill hover:opacity-90 transition-opacity"
            >
              <Plus size={18} aria-hidden />
              新增
            </Link>
          </div>
        }
      />

      <PageContainer vertical="default">
        <PullToRefreshBar
          onRefresh={refresh}
          data-testid="diaries-pull-to-refresh"
        >
          <SectionStack density="default">
            <div className="flex items-center justify-between gap-s-3">
              <DiaryFilter
                scope={scope}
                onScopeChange={setScope}
                hidePartnerChip={!pairId}
              />
            </div>

            {liveQuery.isLoading ? (
              <LoadingState count={4} shape="row" />
            ) : liveQuery.isError ? (
              <ErrorState
                title="日記載入失敗"
                retryLabel="重試"
                onRetry={() => void liveQuery.refetch()}
              />
            ) : totalLoaded === 0 ? (
              <EmptyState
                title="還沒有日記"
                description="寫下今天的第一篇吧"
                cta={
                  <Link to="/diaries/new">
                    <Button variant="primary">新增第一篇</Button>
                  </Link>
                }
              />
            ) : totalShown === 0 ? (
              <EmptyState
                title={scope === "mine" ? "你還沒寫日記" : scope === "partner" ? "對方還沒寫日記" : "沒有符合的日記"}
                description={
                  scope === "with-private"
                    ? "切換其他範圍, 或寫一篇新的"
                    : "切換到「全部」看看"
                }
              />
            ) : (
              <RowGap density="default" as="ul" className="flex flex-col">
                {items.map((d) => (
                  <li key={d.id}>
                    <DiaryCard diary={d} isAuthor={user?.id === d.author_id} />
                  </li>
                ))}
              </RowGap>
            )}

            {liveQuery.hasNextPage ? (
              <div className="flex justify-center pt-s-2">
                <Button
                  variant="ghost"
                  onClick={() => liveQuery.fetchNextPage()}
                  disabled={liveQuery.isFetchingNextPage}
                  data-testid="load-more"
                  leadingIcon={
                    liveQuery.isFetchingNextPage ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : null
                  }
                >
                  {liveQuery.isFetchingNextPage ? "載入中…" : "載入更多"}
                </Button>
              </div>
            ) : null}
          </SectionStack>
        </PullToRefreshBar>
      </PageContainer>
    </div>
  );
}
